<template>
  <a-drawer width="85%" :visible="open" @close="onClose" :destroyOnClose="true">
    <a-divider orientation="left">
      <b>{{ formTitle }}</b>
    </a-divider>
    <a-form-model ref="form" :model="form" :rules="rules">
      <a-form-model-item :label="labels.sort" prop="sort">
        <a-input-number v-model="form.sort" :placeholder="labels.sortPlaceholder" />
      </a-form-model-item>

      <a-tabs v-model="activeLang">
        <a-tab-pane
          v-for="item in languageFields"
          :key="item.key"
          :tab="item.label"
        >
          <a-form-model-item
            :label="item.label + labels.titleSuffix"
            :prop="item.titleProp"
            :rules="item.key === 'zh' ? [{ required: true, message: labels.titleRequired, trigger: 'blur' }] : []"
          >
            <a-input v-model="form[item.titleProp]" :placeholder="labels.inputPrefix + item.label + labels.titleSuffix" />
          </a-form-model-item>
          <a-form-model-item
            :label="item.label + labels.contentSuffix"
            :prop="item.contentProp"
            :rules="item.key === 'zh' ? [{ required: true, message: labels.contentRequired, trigger: 'blur' }] : []"
          >
            <editor :editorValue="item.contentProp" v-model="form[item.contentProp]" />
          </a-form-model-item>
        </a-tab-pane>
      </a-tabs>

      <div class="bottom-control">
        <a-space>
          <a-button type="primary" :loading="submitLoading" @click="submitForm">
            {{ labels.save }}
          </a-button>
          <a-button type="dashed" @click="cancel">
            {{ labels.cancel }}
          </a-button>
        </a-space>
      </div>
    </a-form-model>
  </a-drawer>
</template>

<script>
import { getHelp, addHelp, updateHelp } from '@/api/help/help'
import Editor from '@/components/Editor'

const LANGUAGE_FIELDS = [
  { key: 'zh', label: '\u4e2d\u6587\u7b80\u4f53', titleProp: 'titleCn', contentProp: 'contentCn' },
  { key: 'en', label: '\u82f1\u6587', titleProp: 'titleEn', contentProp: 'contentEn' },
  { key: 'ab', label: '\u963f\u62c9\u4f2f\u8bed', titleProp: 'titleAb', contentProp: 'contentAb' },
  { key: 'tc', label: '\u4e2d\u6587\u7e41\u4f53', titleProp: 'title', contentProp: 'content' },
  { key: 'ko', label: '\u97e9\u8bed', titleProp: 'titleKo', contentProp: 'contentKo' },
  { key: 'vi', label: '\u8d8a\u5357\u8bed', titleProp: 'titleVi', contentProp: 'contentVi' },
  { key: 'fr', label: '\u6cd5\u8bed', titleProp: 'titleBm', contentProp: 'contentBm' },
  { key: 'jp', label: '\u65e5\u8bed', titleProp: 'titleIn', contentProp: 'contentIn' }
]

const LABELS = {
  sort: '\u6392\u5e8f',
  sortPlaceholder: '\u8bf7\u8f93\u5165\u6392\u5e8f',
  titleSuffix: '\u6807\u9898',
  contentSuffix: '\u5185\u5bb9',
  titleRequired: '\u7b80\u4f53\u6807\u9898\u4e0d\u80fd\u4e3a\u7a7a',
  contentRequired: '\u7b80\u4f53\u5185\u5bb9\u4e0d\u80fd\u4e3a\u7a7a',
  inputPrefix: '\u8bf7\u8f93\u5165',
  save: '\u4fdd\u5b58',
  cancel: '\u53d6\u6d88',
  addTitle: '\u6dfb\u52a0\u5e2e\u52a9',
  editTitle: '\u4fee\u6539\u5e2e\u52a9',
  addOk: '\u65b0\u589e\u6210\u529f',
  editOk: '\u4fee\u6539\u6210\u529f'
}

const emptyForm = () => ({
  id: null,
  sort: 0,
  title: '',
  titleCn: '',
  titleEn: '',
  titleBm: '',
  titleIn: '',
  titleAb: '',
  titleVi: '',
  titleKo: '',
  content: '',
  contentCn: '',
  contentEn: '',
  contentBm: '',
  contentIn: '',
  contentAb: '',
  contentVi: '',
  contentKo: '',
  remark: null
})

export default {
  name: 'CreateForm',
  components: { Editor },
  data () {
    return {
      submitLoading: false,
      formTitle: '',
      activeLang: 'zh',
      languageFields: LANGUAGE_FIELDS,
      labels: LABELS,
      form: emptyForm(),
      formType: 1,
      open: false,
      rules: {
        sort: [{ required: true, message: '\u6392\u5e8f\u4e0d\u80fd\u4e3a\u7a7a' }]
      }
    }
  },
  methods: {
    onClose () {
      this.open = false
    },
    cancel () {
      this.open = false
      this.reset()
    },
    reset () {
      this.formType = 1
      this.activeLang = 'zh'
      this.form = emptyForm()
    },
    handleAdd () {
      this.reset()
      this.formType = 1
      this.open = true
      this.formTitle = LABELS.addTitle
    },
    handleUpdate (row, ids) {
      this.reset()
      this.formType = 2
      const id = row ? row.id : ids
      getHelp({ id }).then(response => {
        const data = response.data || {}
        this.form = {
          ...emptyForm(),
          ...data,
          title: data.title || '',
          titleCn: data.titleCn || data.title || '',
          titleEn: data.titleEn || '',
          titleBm: data.titleBm || '',
          titleIn: data.titleIn || '',
          titleAb: data.titleAb || '',
          titleVi: data.titleVi || '',
          titleKo: data.titleKo || '',
          content: data.content || '',
          contentCn: data.contentCn || data.content || '',
          contentEn: data.contentEn || '',
          contentBm: data.contentBm || '',
          contentIn: data.contentIn || '',
          contentAb: data.contentAb || '',
          contentVi: data.contentVi || '',
          contentKo: data.contentKo || ''
        }
        this.open = true
        this.formTitle = LABELS.editTitle
      })
    },
    submitForm () {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return false
        }
        if (!this.form.title && this.form.titleCn) {
          this.form.title = this.form.titleCn
        }
        if (!this.form.content && this.form.contentCn) {
          this.form.content = this.form.contentCn
        }
        this.submitLoading = true
        const req = this.form.id ? updateHelp(this.form) : addHelp(this.form)
        req.then(() => {
          this.$message.success(this.form.id ? LABELS.editOk : LABELS.addOk, 3)
          this.open = false
          this.$emit('ok')
        }).finally(() => {
          this.submitLoading = false
        })
      })
    }
  }
}
</script>
